import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { auth, db, resolveWorkspace, activateWorkspace } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from '../services/auditedFirestore';
import { MODULES, SUPER_ROLES } from '../utils/navigation';

const AuthContext = createContext();
export function useAuth() { return useContext(AuthContext); }
export const ACCESS_CHECK_TIMEOUT_MS = 20000;
const managementRights = Object.fromEntries([...MODULES.filter(m => m.feature !== 'accessAuditTrail').map(m => m.feature), 'accessIR_Finance', 'accessIR_IT', 'accessIR_Academics'].map(feature => [feature, true]));
const pendingSession = { user: null, role: null, permissions: {}, loading: true, loadingStage: 'session', error: null };
const accessError = (code, message) => Object.assign(new Error(message), { code });
const recoverableAccessErrors = new Set(['access/session-timeout', 'access/profile-timeout', 'unavailable', 'deadline-exceeded', 'auth/network-request-failed']);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(pendingSession);
  const [attempt, setAttempt] = useState(0);
  const retryAccess = useCallback(() => setAttempt(value => value + 1), []);
  useEffect(() => {
    let disposed = false;
    let generation = 0;
    let timer;
    let unsubscribeAuth = () => {};
    let unsubscribeProfile = () => {};
    const fail = (user, error) => {
      clearTimeout(timer);
      if (!disposed) setSession({ user, role: null, permissions: {}, loading: false, loadingStage: null, error });
    };
    const startTimeout = (user, stage, version) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!disposed && generation === version) fail(user, accessError(`access/${stage}-timeout`, stage === 'session'
          ? 'Sign-in is taking too long. Check your internet connection and try again.'
          : 'Your account permissions could not be loaded in time. Check your internet connection and try again.'));
      }, ACCESS_CHECK_TIMEOUT_MS);
    };
    setSession(pendingSession);
    startTimeout(null, 'session', generation);
    try {
      unsubscribeAuth = onAuthStateChanged(auth, async user => {
        if (disposed) return;
        const version = ++generation;
        clearTimeout(timer);
        unsubscribeProfile();
        unsubscribeProfile = () => {};
        setSession({ user, role: null, permissions: {}, loading: true, loadingStage: user ? 'profile' : 'session', error: null });
        if (user) startTimeout(user, 'profile', version);
        let workspace;
        try { workspace = await resolveWorkspace(user); }
        catch (error) { if (!disposed && generation === version) fail(user, error); return; }
        if (disposed || generation !== version) return;
        activateWorkspace(workspace);
        if (!user) { setSession({ user: null, role: null, permissions: {}, loading: false, loadingStage: null, error: null }); return; }
        const profile = doc(db, 'users', user.uid);
        try {
          unsubscribeProfile = onSnapshot(profile, snapshot => {
            if (disposed || generation !== version) return;
            clearTimeout(timer);
            if (!snapshot.exists()) {
              fail(user, accessError('access/profile-missing', 'Your ERP account profile was not found. Ask your administrator to check your account.'));
              return;
            }
            const data = snapshot.data() || {};
            const erpInactive = ['deleting', 'deleted', 'archived', 'disabled', 'inactive'].includes(data.erpStatus);
            setSession({ user, role: erpInactive ? null : data.role || 'employee', permissions: erpInactive ? {} : SUPER_ROLES.includes(data.role) ? { ...managementRights, accessAuditTrail: data.accessRights?.accessAuditTrail === true } : data.accessRights || {}, isReview: workspace.isReview, erpInactive, erpArchived: data.erpStatus === 'archived', hrArchived: data.hrStatus === 'archived', loading: false, loadingStage: null, error: null });
          }, error => {
            if (!disposed && generation === version) fail(user, error);
          });
        } catch (error) { fail(user, error); }

      }, error => {
        generation++;
        unsubscribeProfile();
        fail(null, error);
      });
    } catch (error) { fail(null, error); }
    return () => { disposed = true; generation++; clearTimeout(timer); unsubscribeProfile(); unsubscribeAuth(); };
  }, [attempt]);
  useEffect(() => {
    if (!recoverableAccessErrors.has(session.error?.code)) return;
    let retried = false;
    const recover = () => {
      if (retried || navigator.onLine === false || document.visibilityState === 'hidden') return;
      retried = true;
      retryAccess();
    };
    // A suspended phone or interrupted connection can leave a terminated profile
    // listener behind. Retry verification on return, without signing anyone out
    // or reusing permissions that failed verification.
    window.addEventListener('online', recover);
    window.addEventListener('focus', recover);
    document.addEventListener('visibilitychange', recover);
    return () => {
      window.removeEventListener('online', recover);
      window.removeEventListener('focus', recover);
      document.removeEventListener('visibilitychange', recover);
    };
  }, [session.error, retryAccess]);
  const value = useMemo(() => ({ ...session, retryAccess }), [session, retryAccess]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
